import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell,
} from "recharts";
import api from "@/lib/api";
import { eur, num, dtNL, dateNL, RANGES } from "@/lib/format";
import { PageHeader, Card, Badge, fmtDate } from "./ui";
import {
  TrendingUp, TrendingDown, Package, Receipt, CreditCard, ShieldAlert,
  XCircle, Wallet, UserPlus, Repeat, Loader2,
} from "lucide-react";

const COLORS = ["#FF8C00", "#FFD700", "#00CED1", "#B5179E", "#B19CD9", "#FF3B2F", "#4ADE80", "#F87171"];

function Delta({ pct }) {
  if (pct == null) return <span className="text-xs text-white/30">—</span>;
  const up = pct >= 0;
  return (
    <span className={`flex items-center gap-1 text-xs font-medium ${up ? "text-emerald-400" : "text-red-400"}`}>
      {up ? <TrendingUp size={13} /> : <TrendingDown size={13} />}{Math.abs(pct)}%
    </span>
  );
}

function Kpi({ icon: Icon, label, value, pct, color, onClick, testid, sub }) {
  return (
    <Card className={`p-5 transition-colors ${onClick ? "cursor-pointer hover:border-sfc-orange/40" : ""}`}>
      <div data-testid={testid} onClick={onClick} className="h-full">
        <div className="flex items-center justify-between">
          <Icon className={color} size={20} />
          {pct !== undefined && <Delta pct={pct} />}
        </div>
        <p className="mt-3 font-display text-2xl text-white lg:text-3xl">{value}</p>
        <p className="text-xs text-white/50">{label}</p>
        {sub && <p className="mt-1 text-xs text-white/35">{sub}</p>}
      </div>
    </Card>
  );
}

export default function Dashboard() {
  const [range, setRange] = useState("this_week");
  const [custom, setCustom] = useState({ start: "", end: "" });
  const [d, setD] = useState(null);
  const [series, setSeries] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [top, setTop] = useState([]);
  const [activity, setActivity] = useState([]);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [metric, setMetric] = useState("inc"); // inc | ex | vat
  const navigate = useNavigate();

  const params = useCallback(() => {
    const p = { range };
    if (range === "custom" && custom.start && custom.end) { p.start = custom.start; p.end = custom.end; }
    return p;
  }, [range, custom]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = params();
      const [dd, rv, st, tp, ac, ro] = await Promise.all([
        api.get("/admin/dashboard", { params: p }),
        api.get("/admin/dashboard/revenue", { params: p }),
        api.get("/admin/dashboard/order-statuses"),
        api.get("/admin/dashboard/top-products", { params: { ...p, metric: "qty" } }),
        api.get("/admin/dashboard/activity"),
        api.get("/admin/orders"),
      ]);
      setD(dd.data); setSeries(rv.data); setStatuses(st.data); setTop(tp.data);
      setActivity(ac.data); setRecent(ro.data.slice(0, 10));
    } catch { /* noop */ } finally { setLoading(false); }
  }, [params]);

  useEffect(() => { if (range !== "custom" || (custom.start && custom.end)) load(); }, [load, range, custom]);

  const k = d?.kpis || {};
  const rangeLabel = RANGES.find((x) => x.key === range)?.label || "";
  const metricKey = metric;
  const metricLabel = { inc: "incl. btw", ex: "excl. btw", vat: "btw" }[metric];

  return (
    <div>
      <PageHeader title="Dashboard" subtitle={d ? `${dtNL(d.now)} · Europe/Amsterdam` : "Laden..."}>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex rounded-full border border-white/10 bg-white/[0.03] p-1">
            {RANGES.map((r) => (
              <button
                key={r.key}
                data-testid={`range-${r.key}`}
                onClick={() => setRange(r.key)}
                className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${range === r.key ? "bg-sfc-orange text-white" : "text-white/60 hover:text-white"}`}
              >
                {r.label}
              </button>
            ))}
          </div>
          {loading && <Loader2 className="animate-spin text-sfc-orange" size={18} />}
        </div>
      </PageHeader>

      {range === "custom" && (
        <div className="mb-6 flex flex-wrap items-center gap-3">
          <input data-testid="custom-start" type="date" value={custom.start} onChange={(e) => setCustom({ ...custom, start: e.target.value })} className="rounded-xl border border-white/10 bg-sfc-night px-3 py-2 text-sm text-white" />
          <span className="text-white/40">tot</span>
          <input data-testid="custom-end" type="date" value={custom.end} onChange={(e) => setCustom({ ...custom, end: e.target.value })} className="rounded-xl border border-white/10 bg-sfc-night px-3 py-2 text-sm text-white" />
        </div>
      )}

      {/* KPI grid */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-3 xl:grid-cols-5">
        <Kpi testid="kpi-revenue-inc" icon={Wallet} color="text-sfc-orange" label={`Omzet incl. btw (${rangeLabel})`} value={eur(k.revenue_inc)} pct={k.revenue_inc_pct} onClick={() => navigate("/admin/insights")} />
        <Kpi testid="kpi-revenue-ex" icon={Receipt} color="text-sfc-cyan" label={`Omzet excl. btw (${rangeLabel})`} value={eur(k.revenue_ex)} pct={k.revenue_ex_pct} onClick={() => navigate("/admin/insights")} />
        <Kpi testid="kpi-vat" icon={Receipt} color="text-sfc-yellow" label="Btw te dragen" value={eur(k.vat)} pct={k.vat_pct} onClick={() => navigate("/admin/insights")} sub={k.vat_breakdown?.map((v) => `${v.rate}%: ${eur(v.amount)}`).join(" · ")} />
        <Kpi testid="kpi-paid-orders" icon={Package} color="text-emerald-400" label="Betaalde bestellingen" value={num(k.paid_orders)} pct={k.paid_orders_pct} onClick={() => navigate("/admin/orders?status=paid")} />
        <Kpi testid="kpi-aov" icon={TrendingUp} color="text-sfc-lilac" label="Gem. orderwaarde" value={eur(k.avg_order_value)} pct={k.avg_order_value_pct} />
        <Kpi testid="kpi-open-payments" icon={CreditCard} color="text-sfc-yellow" label="Openstaande betalingen" value={num(k.open_payments_count)} sub={eur(k.open_payments_amount)} onClick={() => navigate("/admin/payments")} />
        <Kpi testid="kpi-verification" icon={ShieldAlert} color="text-sfc-orange" label="In bedrijfsverificatie" value={num(k.orders_in_verification)} onClick={() => navigate("/admin/verificaties")} />
        <Kpi testid="kpi-failed" icon={XCircle} color="text-red-400" label="Mislukte/verlopen betalingen" value={num(k.failed_payments_count)} sub={eur(k.failed_payments_amount)} onClick={() => navigate("/admin/payments")} />
        <Kpi testid="kpi-new-customers" icon={UserPlus} color="text-sfc-cyan" label="Nieuwe zakelijke klanten" value={num(k.new_customers)} onClick={() => navigate("/admin/klanten")} />
        <Kpi testid="kpi-subscriptions" icon={Repeat} color="text-sfc-lilac" label="Actieve abonnementen" value={d?.subscriptions_enabled ? num(k.active_subscriptions) : "—"} sub={d?.subscriptions_enabled ? eur(k.mrr) + " MRR" : "Niet geactiveerd"} onClick={() => navigate("/admin/abonnementen")} />
      </div>

      {/* Charts */}
      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        <Card className="p-6 lg:col-span-2">
          <div className="flex items-center justify-between">
            <h3 className="font-display text-lg text-white">Omzetontwikkeling</h3>
            <div className="flex rounded-full border border-white/10 p-1 text-xs">
              {[["inc", "Incl. btw"], ["ex", "Excl. btw"], ["vat", "Btw"]].map(([key, lbl]) => (
                <button key={key} data-testid={`metric-${key}`} onClick={() => setMetric(key)} className={`rounded-full px-3 py-1 ${metric === key ? "bg-sfc-orange text-white" : "text-white/60"}`}>{lbl}</button>
              ))}
            </div>
          </div>
          {series.some((s) => s.inc || s.ex || s.vat) ? (
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={series} margin={{ top: 20, right: 10, left: -10, bottom: 0 }}>
                <defs>
                  <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#FF8C00" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#FF8C00" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" />
                <XAxis dataKey="date" tick={{ fill: "#ffffff60", fontSize: 11 }} tickFormatter={(v) => v.slice(5)} />
                <YAxis tick={{ fill: "#ffffff60", fontSize: 11 }} tickFormatter={(v) => `€${v}`} />
                <Tooltip contentStyle={{ background: "#0e0722", border: "1px solid #ffffff20", borderRadius: 12, color: "#fff" }}
                  formatter={(v, n) => [eur(v), { inc: "Incl. btw", ex: "Excl. btw", vat: "Btw" }[n] || n]} />
                <Area type="monotone" dataKey={metricKey} stroke="#FF8C00" strokeWidth={2} fill="url(#rev)" name={metricLabel} />
              </AreaChart>
            </ResponsiveContainer>
          ) : <p className="py-20 text-center text-white/40">Nog geen betaalde bestellingen in deze periode.</p>}
        </Card>

        <Card className="p-6">
          <h3 className="font-display text-lg text-white">Bestellingen per status</h3>
          {statuses.length ? (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart layout="vertical" data={statuses} margin={{ left: 20, right: 10 }}>
                <XAxis type="number" hide />
                <YAxis type="category" dataKey="status" width={130} tick={{ fill: "#ffffff70", fontSize: 10 }} tickFormatter={(v) => v.replace(/_/g, " ")} />
                <Tooltip contentStyle={{ background: "#0e0722", border: "1px solid #ffffff20", borderRadius: 12 }} cursor={{ fill: "#ffffff08" }} />
                <Bar dataKey="count" radius={[0, 6, 6, 0]} onClick={(e) => navigate(`/admin/orders?status=${e.status}`)} className="cursor-pointer">
                  {statuses.map((s, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : <p className="py-20 text-center text-white/40">Geen bestellingen.</p>}
        </Card>

        <Card className="p-6">
          <h3 className="font-display text-lg text-white">Populaire smaken</h3>
          {top.length ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart layout="vertical" data={top} margin={{ left: 10, right: 10 }}>
                <XAxis type="number" hide />
                <YAxis type="category" dataKey="name" width={110} tick={{ fill: "#ffffff70", fontSize: 11 }} />
                <Tooltip contentStyle={{ background: "#0e0722", border: "1px solid #ffffff20", borderRadius: 12 }} cursor={{ fill: "#ffffff08" }} formatter={(v) => [num(v), "Aantal"]} />
                <Bar dataKey="qty" radius={[0, 6, 6, 0]} onClick={() => navigate("/admin/producten")} className="cursor-pointer">
                  {top.map((s, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : <p className="py-16 text-center text-white/40">Nog geen verkopen.</p>}
        </Card>

        <Card className="p-6 lg:col-span-2">
          <h3 className="font-display text-lg text-white">Recente activiteit</h3>
          <div className="mt-4 space-y-2">
            {activity.length ? activity.map((a) => (
              <div key={a.id} className="flex items-center justify-between rounded-lg bg-white/[0.03] px-3 py-2 text-xs">
                <span className="text-white/75">{a.action.replace(/_/g, " ")} · <span className="text-white/45">{a.entity_type}</span></span>
                <span className="text-white/40">{fmtDate(a.created_at)}</span>
              </div>
            )) : <p className="py-8 text-center text-white/40">Geen recente activiteit.</p>}
          </div>
        </Card>
      </div>

      {/* Recent orders */}
      <Card className="mt-6">
        <div className="flex items-center justify-between p-6 pb-3">
          <h3 className="font-display text-lg text-white">Recente bestellingen</h3>
          <button onClick={() => navigate("/admin/orders")} className="rounded-full border border-white/15 px-4 py-2 text-xs text-white/80 hover:bg-white/10">Bekijk alle</button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="border-b border-white/10"><tr>
              {["Ordernr", "Datum", "Bedrijf", "Totaal", "Verificatie", "Betaling", "Status"].map((h) => (
                <th key={h} className="whitespace-nowrap px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-white/45">{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {recent.map((o) => (
                <tr key={o.id} data-testid={`recent-order-${o.order_number}`} onClick={() => navigate(`/admin/orders/${o.id}`)} className="cursor-pointer border-b border-white/5 text-sm text-white/80 hover:bg-white/[0.03]">
                  <td className="px-4 py-3 font-medium text-sfc-orange">{o.order_number}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-white/50">{dtNL(o.created_at)}</td>
                  <td className="px-4 py-3">{o.customer.company_name}</td>
                  <td className="whitespace-nowrap px-4 py-3">{eur(o.totals.total_inc_vat)}</td>
                  <td className="px-4 py-3"><Badge status={o.business_verification_id ? "approved" : "pending"} /></td>
                  <td className="px-4 py-3"><Badge status={o.status === "paid" ? "paid" : o.status === "pending_payment" ? "open" : "pending"} /></td>
                  <td className="px-4 py-3"><Badge status={o.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
          {recent.length === 0 && <p className="py-14 text-center text-white/40">Nog geen bestellingen.</p>}
        </div>
      </Card>
    </div>
  );
}
